import type { JobCardDataFragment } from '../../../graphQL/fragments/__generated__/jobCardData.gqlgenerated';
import type { AppearanceMode } from '../JobCard';

import { getLocationText } from './getLocationText';
import { useLogFeatureDisplay } from '../../../../homepage/logging/DisplayLog';
import { Car, Text , Box } from '../../../../mads';
import { useI18n } from '../../../../shared/I18n';

import { colors } from '@indeed/ifl-tokens';
import { useEffect } from 'react';
import { type AccessibilityProps, StyleSheet } from 'react-native';

interface LocationComponentProps extends AccessibilityProps {
    commuteTimeMinutes?: number;
    location: JobCardDataFragment['location']['formatted'];
    jobKey: string;
    appearanceMode: AppearanceMode;
    attributes?: JobCardDataFragment['attributes'];
}

export function LocationComponent({
    commuteTimeMinutes,
    location,
    jobKey,
    appearanceMode,
    attributes,
    ...props
}: LocationComponentProps) {
    const i18n = useI18n();
    const logFeatureDisplay = useLogFeatureDisplay();

    useEffect(() => {
        if (commuteTimeMinutes) {
            logFeatureDisplay({
                jobKey: jobKey,
                field: 'commuteEstimate',
                value: commuteTimeMinutes,
            });
        }
    }, [commuteTimeMinutes, jobKey, logFeatureDisplay]);

    return (
        <Box sx={styles.container} {...props}>
            {commuteTimeMinutes && (
                <>
                    <Car size="sm" fill={colors.foreground['[base]']} />
                    <Text level={2} sx={styles.commuteTime}>
                        {commuteTimeMinutes > 90
                            ? i18n.gettext('90+ min')
                            : i18n.pgettext(
                                  'commute time estimate',
                                  '{0} min',
                                  commuteTimeMinutes.toString(),
                              )}
                    </Text>
                    <Text level={2} sx={styles.commuteDot}>·</Text>
                </>
            )}
            <Text
                sx={appearanceMode === 'default' ? styles.locationDefaultJobCard : styles.location}
            >
                {commuteTimeMinutes ? location.short : getLocationText(location.long, i18n, attributes)}
            </Text>
        </Box>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    commuteTime: {
        marginLeft: 4,
        lineHeight: 16,
        color: colors.foreground['[base]'],
    },
    commuteDot: {
        paddingInline: 4,
        lineHeight: 16,
        color: colors.foreground['[base]'],
    },
    location: {
        color: colors.foreground['[base]'],
    },
    locationDefaultJobCard: {
        color: colors.foreground['[base]'],
        fontSize: 14,
        lineHeight: 16,
    },
});
